import { inject, Injectable } from "@angular/core";
import { EnvironmentConfig } from "@keus-template/environment";
import {
  AuthenticationDetails,
  ClientMetadata,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import * as AWS from "aws-sdk/global";
import {
  BehaviorSubject,
  distinctUntilChanged,
  Observable,
  Observer,
} from "rxjs";
import {
  ConfirmPasswordEnum,
  ResetPasswordEnum,
  SignInEnum,
  SignUpEnum,
} from "../enums/authenticate.enum";
import {
  CognitoConfirmPasswordResult,
  CognitoResetPasswordResult,
  CognitoSignInResult,
  CognitoSignUpResult,
  FederatedSignInResult,
} from "../interfaces/auth.interfaces";
import { CognitoUtilsService } from "./cognito-utils.service";
import { GoogleAuthService } from "./google-auth.service";

@Injectable({
  providedIn: "root",
})
export class AuthService extends GoogleAuthService {
  /** Environment configuration */
  private readonly environment = inject(EnvironmentConfig);

  /** Cognito utilities service */
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);

  /** The authentication status subject */
  private readonly _isAuthenticatedSubject = new BehaviorSubject(false);

  /** The authentication status observable */
  private readonly _isAuthenticated$ = this._isAuthenticatedSubject.pipe(
    distinctUntilChanged(),
  );

  /** The authenticated user */
  private _cognitoUser: CognitoUser | undefined;

  /** Temporary cache for credentials during TOTP flow */
  private _cachedCredentials: { email: string; password: string } | null = null;

  /**
   * Constructor a new instance of AuthService
   */
  constructor() {
    super();
    this._checkUserSession();
  }

  /**
   * Sign in a user
   * @param email the user's email
   * @param password the user's password
   * @returns an observable of the authentication result
   */
  public signIn$(
    email: string,
    password: string,
  ): Observable<CognitoSignInResult> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    this._cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });
    
    return new Observable<CognitoSignInResult>(
      (observer: Observer<CognitoSignInResult>) => {
        this._cognitoUser?.authenticateUser(authenticationDetails, {
          onSuccess: (result: CognitoUserSession) => {
            this._onLoginSuccess(result, observer);
          },
          // First time login attempt
          newPasswordRequired: () => {
            observer.next({ type: SignInEnum.NEW_PASSWORD_REQUIRED });
            observer.complete();
          },
          totpRequired: (challengeName, challengeParameters) => {
            // Cache credentials for automatic retry after TOTP
            this._cachedCredentials = { email, password };
            observer.next({
              type: SignInEnum.TOTP_REQUIRED,
              result: { challengeName, challengeParameters },
            });
            observer.complete();
          },
          onFailure: (error: Error) => {
            observer.error({ type: SignInEnum.FAIL, result: error });
          },
        });
      },
    );
  }

  /**
   * Resets the password for a user
   * @param username the username of the user
   * @returns an observable that emits the result of the password reset
   */
  public resetPassword$(
    username: string,
  ): Observable<CognitoResetPasswordResult> {
    const userData = {
      Username: username,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoResetPasswordResult>((observer) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          observer.next({ type: ResetPasswordEnum.SUCCESS });
          observer.complete();
        },
        inputVerificationCode: () => {
          observer.next({ type: ResetPasswordEnum.INPUT_VERIFICATION_CODE });
          observer.complete();
        },
        onFailure: (error) => {
          observer.error({ type: ResetPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  /**
   * Confirms the new password for a user
   * @param email the email of the user
   * @param verificationCode the verification code sent to the user's email
   * @param password the new password to set
   * @returns an observable that emits the result of the password confirmation
   */
  public confirmNewPassword$(
    email: string,
    verificationCode: string,
    password: string,
  ): Observable<CognitoConfirmPasswordResult> {
    const userData = {
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoConfirmPasswordResult>((observer) => {
      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess: (result) => {
          observer.next({ type: ConfirmPasswordEnum.SUCCESS, result });
          observer.complete();
        },
        onFailure: (error: Error) => {
          observer.error({ type: ResetPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  public changePassword$(
    oldPassword: string,
    newPassword: string,
  ): Observable<CognitoResetPasswordResult> {
    const cognitoUser = this._cognitoUtilsService.getCurrentUser();

    return new Observable<CognitoResetPasswordResult>((observer) => {
      cognitoUser?.getSession(
        (error: Error | null, session: CognitoUserSession | null) =>
          cognitoUser?.changePassword(
            oldPassword,
            newPassword,
            (err, result) => {
              if (err) {
                console.error("Error during change password", err);
                observer.error({ type: ResetPasswordEnum.FAIL, result: err });
              } else {
                observer.next({ type: ResetPasswordEnum.SUCCESS });
                observer.complete();
              }
            },
          ),
      );
    });
  }

  /**
   * Logs out the user
   * Clears Cognito session and all cached auth state including sessionStorage
   */
  public logout(): void {
    const poolData = {
      UserPoolId: this.environment.cognito.userPoolId,
      ClientId: this.environment.cognito.userPoolClientId,
    };
    const userPool = new CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser() ?? undefined;
    if (cognitoUser) {
      cognitoUser.signOut(() => this._checkUserSession());
    } else {
      this._isAuthenticatedSubject.next(false);
    }

    // Clear all auth-related sessionStorage to prevent stale state after logout
    try {
      sessionStorage.removeItem('authflow_critical_state');
      sessionStorage.removeItem('temp-questionnaire-data');
    } catch (error) {
      // SessionStorage cleanup failed - non-critical, logout will still proceed
    }

    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  /**
   * Signs up a new user
   * @param email the email of the user
   * @param password the password of the user
   * @returns an observable that emits the result of the sign up
   */
  public signUp$(
    email: string,
    password: string,
  ): Observable<CognitoSignUpResult> {
    // Create CognitoUser for later use in confirmation
    this._cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });

    return new Observable<CognitoSignUpResult>((observer) => {
      this._cognitoUtilsService
        .getUserPool()
        .signUp(email, password, [], [], (error) => {
          if (error) {
            console.error("error", error); // gestire errore di utente già creato nel componente sign-in-and-sign-up
            observer.error({ type: SignUpEnum.FAIL, result: error });
          } else {
            // Cache credentials for automatic login after confirmation
            this._cachedCredentials = { email, password };
            observer.next({ type: SignUpEnum.SUCCESS });
            observer.complete();
          }
        });
    });
  }

  /**
   * Confirms the sign up of a new user and automatically attempts login
   * @param confirmationCode the confirmation code sent to the user's email
   * @returns an observable that emits the result of the confirmation and auto-login
   */
  public confirmSignUp$(
    confirmationCode: string,
  ): Observable<CognitoSignInResult> {
    const clientMetadata: ClientMetadata = {
      group: "test",
    };
    if (!this._cognitoUser) {
      console.error("❌ [AuthService] Cognito user is not defined");
      return new Observable<CognitoSignInResult>((observer) => {
        observer.error(new Error("Cognito user is not defined"));
      });
    }
    
    
    // if the confirm sign up is called the cognito user must be defined
    return new Observable<CognitoSignInResult>((observer) => {
      this._cognitoUser?.confirmRegistration(
        confirmationCode,
        true,
        (error) => {
          if (error) {
            console.error("❌ [AuthService] confirmRegistration error:", error);
            observer.error(error);
          } else {
            // After successful confirmation, attempt automatic login with cached credentials
            if (this._cachedCredentials) {
              const { email, password } = this._cachedCredentials;

              // Use the existing signIn$ method for automatic login
              this.signIn$(email, password).subscribe({
                next: (result) => {
                  observer.next(result);
                  observer.complete();
                },
                error: (error) => {
                  console.error("❌ [AuthService] Auto-login failed:", error);
                  observer.error(error);
                },
              });
            } else {
              // No cached credentials, just confirm the signup was successful
              observer.next({ type: SignInEnum.SUCCESS });
              observer.complete();
            }
          }
        },
        clientMetadata,
      );
    });
  }

  /**
   * Resends the confirmation code to the user's email.
   * @returns an observable that emits the result of the confirmation
   */
  public resendCode$(): Observable<void> {
    // if the confirm sign up is called the cognito, user must be defined
    return new Observable<void>((observer) => {
      this._cognitoUser?.resendConfirmationCode((error) => {
        if (error) {
          observer.error(error);
        } else {
          observer.next();
          observer.complete();
        }
      });
    });
  }

  /**
   * Checks if the user is authenticated.
   * @returns an observable that emits the result of the authentication check
   */
  public isAuthenticated$(): Observable<boolean> {
    this._checkUserSession(); // trigger the check manually
    return this._isAuthenticated$;
  }

  /**
   * Handles the successful login.
   * @param session the user's session
   * @param observer the observer to notify
   */
  private _onLoginSuccess(
    session: CognitoUserSession,
    observer: Observer<CognitoSignInResult>,
  ): void {
    AWS.config.credentials = this._cognitoUtilsService.buildCognitoCreds(
      session.getIdToken().getJwtToken(),
    );
    observer.next({ type: SignInEnum.SUCCESS, result: session });
    observer.complete();
    this._checkUserSession();
  }

  /**
   * Checks the user's session.
   */
  private _checkUserSession(): void {
    const cognitoUser = this._cognitoUtilsService.getCurrentUser();
    if (!cognitoUser) {
      this._isAuthenticatedSubject.next(false);
      return;
    }

    cognitoUser.getSession(
      (error: Error | null, session: CognitoUserSession | null) => {
        if (session?.isValid()) {
          this._isAuthenticatedSubject.next(true);
        } else {
          this._isAuthenticatedSubject.next(false);
        }
      },
    );
  }

  /**
   * Inizia il flusso di login con Google
   * Reindirizza l'utente alla pagina di login di Google
   */
  public signInWithGoogle(): void {
    const { region, userPoolClientId, authDomain } = this.environment.cognito;
    super._signInWithGoogle(region, userPoolClientId, authDomain);
  }

  /**
   * Gets the current user role from the stored Cognito session with ultra-robust fallback strategy
   * Uses the EXACT same pattern as AuthInterceptor for maximum reliability
   * @returns The user role or null if no authentication state is available
   */
  public getCurrentUserRole(): string | null {
    try {
      // Use EXACT same pattern as AuthInterceptor - getSession() instead of getSignInUserSession()
      
      // This is a synchronous wrapper around the async getSession() method
      // We'll make a promise and handle it synchronously for now
      let session: any = null;
      let sessionRetrieved = false;
      
      this._cognitoUtilsService.getSession().then(s => {
        session = s;
        sessionRetrieved = true;
      }).catch(error => {
        sessionRetrieved = true;
      });

      // Wait a tiny bit for the async call (not ideal but necessary for sync interface)
      const start = Date.now();
      while (!sessionRetrieved && (Date.now() - start) < 50) {
        // Busy wait for max 50ms
      }

      if (!sessionRetrieved) {
        return null;
      }

      if (!session || !session.isValid()) {
        return null;
      }

      // Extract tokens using same approach as AuthInterceptor
      let idToken: string;
      try {
        idToken = session.getIdToken().getJwtToken();
      } catch (tokenError) {
        return null;
      }

      // Parse role from token
      const role = this._extractRoleFromToken(idToken);
      return role || null;

    } catch (error) {
      return null;
    }
  }

  /**
   * Async version of getCurrentUserRole that properly waits for session
   * @returns Promise<string | null> The user role or null
   */
  public async getCurrentUserRoleAsync(): Promise<string | null> {
    try {
      // Use exact same method as AuthInterceptor
      const session = await this._cognitoUtilsService.getSession();

      if (!session || !session.isValid()) {
        return null;
      }

      // Extract tokens
      const idToken = session.getIdToken().getJwtToken();

      // Parse role from token
      const role = this._extractRoleFromToken(idToken);
      return role || null;

    } catch (error) {
      return null;
    }
  }

  /**
   * Extracts user role from Cognito ID token
   * @param idToken The Cognito ID token (JWT format)
   * @returns The user role or 'patient' as default
   */
  private _extractRoleFromToken(idToken: string): string {
    try {
      // Decode JWT token (format: header.payload.signature)
      const payload = idToken.split(".")[1];
      const decodedPayload = JSON.parse(atob(payload));
      

      // Use same logic as LoggedUserService.getUserIdentifiers$()
      // Check for role in the same order as existing system
      const extractedRole = (
        decodedPayload["userType"] ||
        decodedPayload["cognito:groups"]?.[0] ||
        decodedPayload["custom:role"] ||
        decodedPayload.role ||
        "patient"
      ); // Default fallback
      
      return extractedRole;
    } catch (error) {
      console.error("❌ [AuthService] Failed to extract role from token:", error);
      return "patient"; // Default fallback
    }
  }

  /**
   * Handles the callback after Google login
   * Must be called from the callback page to which the user is redirected after logging in with Google
   * @param code The authorization code returned by Cognito after Google login
   */
  public handleGoogleCallback(code: string): Observable<FederatedSignInResult> {
    const { userPoolClientId, region, authDomain } = this.environment.cognito;

    return new Observable<FederatedSignInResult>((observer) => {
      
      super
        ._fetchGoogleTokens(code, userPoolClientId, region, authDomain)
        .then(({ id_token, access_token, refresh_token }) => {
          
          // Set AWS credentials
          AWS.config.credentials =
            this._cognitoUtilsService.buildCognitoCreds(id_token);

          // Create a CognitoUser object
          const extractedUsername = this._extractUsernameFromToken(id_token);
          
          const userData = {
            Username: extractedUsername,
            Pool: this._cognitoUtilsService.getUserPool(),
          };

          this._cognitoUser = new CognitoUser(userData);

          // Create and set session objects
          const idToken = new CognitoIdToken({ IdToken: id_token });
          const accessToken = new CognitoAccessToken({
            AccessToken: access_token,
          });
          const refreshToken = new CognitoRefreshToken({
            RefreshToken: refresh_token,
          });

          const sessionData = {
            IdToken: idToken,
            AccessToken: accessToken,
            RefreshToken: refreshToken,
          };

          // Set the user session
          const cognitoUserSession = new CognitoUserSession(sessionData);
          this._cognitoUser.setSignInUserSession(cognitoUserSession);

          // Extract user role from ID token
          const userRole = this._extractRoleFromToken(id_token);

          // Update authentication state
          this._isAuthenticatedSubject.next(true);

          const result = {
            type: SignInEnum.SUCCESS,
            result: {
              idToken: id_token,
              accessToken: access_token,
              refreshToken: refresh_token,
            },
            userRole: userRole,
          };

          observer.next(result);
          observer.complete();
        })
        .catch((error) => {
          console.error("❌ [AuthService] Token fetch failed!");
          console.error("🔥 [AuthService] Error in handleGoogleCallback:", error);
          console.error("📊 [AuthService] Error type:", typeof error);
          console.error("📜 [AuthService] Error message:", error?.message);
          console.error("📄 [AuthService] Full error object:", error);
          
          observer.error({ type: SignInEnum.FAIL, result: error });
        });
    });
  }

  /**
   * Completes TOTP authentication and automatically retries login
   * @param totpCode The TOTP code from the user
   * @returns An observable of the authentication result
   */
  public completeTOTP$(totpCode: string): Observable<CognitoSignInResult> {
    if (!this._cognitoUser) {
      throw new Error("No Cognito user found. Please try logging in again.");
    }

    return new Observable<CognitoSignInResult>(
      (observer: Observer<CognitoSignInResult>) => {
        this._cognitoUser?.sendMFACode(
          totpCode,
          {
            onSuccess: (session: CognitoUserSession) => {
              this._onLoginSuccess(session, observer);
              this._clearCachedCredentials();
            },
            onFailure: (error: Error) => {
              observer.error({ type: SignInEnum.FAIL, result: error });
            },
          },
          "SOFTWARE_TOKEN_MFA",
        );
      },
    );
  }

  /**
   * Automatically retries login using cached credentials after successful TOTP
   * @returns An observable of the authentication result or null if no cached credentials
   */
  public autoRetryLoginAfterTOTP$(): Observable<CognitoSignInResult | null> {
    if (!this._cachedCredentials) {
      return new Observable((observer) => {
        observer.next(null);
        observer.complete();
      });
    }

    const { email, password } = this._cachedCredentials;
    return this.signIn$(email, password);
  }

  /**
   * Checks if there are cached credentials available for auto-retry
   * @returns True if credentials are cached, false otherwise
   */
  public hasCachedCredentials(): boolean {
    return this._cachedCredentials !== null;
  }

  /**
   * Clears cached credentials (call this when user manually cancels or logs out)
   */
  public clearCachedCredentials(): void {
    this._clearCachedCredentials();
  }

  /**
   * Private method to clear cached credentials
   */
  private _clearCachedCredentials(): void {
    this._cachedCredentials = null;
  }

  /**
   * Extracts the username from JWT token
   * @param token The JWT token
   * @returns The username
   */
  private _extractUsernameFromToken(token: string): string {
    try {
      // JWT tokens are in format: header.payload.signature
      const payload = token.split(".")[1];
      const decodedPayload = JSON.parse(atob(payload));

      // Cognito tokens might have the username in different fields
      // Commonly it's either 'email', 'cognito:username', or 'sub'
      return (
        decodedPayload.email ||
        decodedPayload["cognito:username"] ||
        decodedPayload.sub ||
        "unknown"
      );
    } catch (error) {
      console.error("Error extracting username from token:", error);
      return "unknown";
    }
  }
}
